import React, { useState, useEffect, useMemo } from 'react'
import { generatePath, useNavigate } from 'react-router-dom'
import {
	useMutation,
	useQuery,
	useQueryClient,
} from '@tanstack/react-query'
import {
	Checkbox,
	FormControl,
	FormControlLabel,
	InputLabel,
	MenuItem,
	Select,
	Typography,
} from '@mui/material'
import { Check } from '@mui/icons-material'
import { useIntl } from 'react-intl'

import Page from '../../../components/Page'
import DataTable from '../../../components/DataTable'
import { ModalRemoveRestoreRecordConfirm } from '../../../components/modals/ModalRemoveRestoreRecordConfirm'
import NotificationExpandedRow from './NotificationExpandedRow'
import withAuth from '../../../components/withAuth'

import {
	notificationTemplateTypes,
	formatDate,
	getErrorMessage,
} from '../../../services/helper'
import { navigationLinking } from '../../../services/navigation'
import queryKeys from '../../../services/queryKeys'
import { eventTypes } from '../../../services/constants'

import { useAlert } from '../../../hooks'
import { useMixpanel } from '../../../hooks/useMixpanel'
import { getTheme } from '../../../config'
import { TheBigPOSApi } from '../../../lib/TheBigPOSClient'

const theme = getTheme()

const AdminNotificationTemplates = () => {
	const { alert } = useAlert()
	const { formatMessage } = useIntl()
	const mixpanel = useMixpanel()
	const navigate = useNavigate()
	const queryClient = useQueryClient()
	const [filteredData, setFilteredData] = useState([])
	const [removeModalVisible, setRemoveModalVisible] = useState(false)
	const [restoreModalVisible, setRestoreModalVisible] =
		useState(false)
	const [activeRow, setActiveRow] = useState()
	const [filters, setFilters] = useState({
		type: 'all',
		borrowerType: 'all',
		active: 'active',
		hideDefaults: false,
	})
	const [searchText, setSearchText] = useState('')

	const columns = [
		{
			name: 'Name',
			selector: (row) => row.description,
			sortable: true,
			minWidth: '35%',
		},
		{
			name: 'Subject',
			selector: (row) => row.subject,
			sortable: true,
		},
		{
			name: 'Status',
			selector: (row) => row.status,
			sortable: true,
		},
		{
			name: 'Send Email',
			selector: (row) => row.emailEnabled,
			cell: (row) => (row.emailEnabled ? <Check /> : '-'),
			sortable: true,
		},
		{
			name: 'Send Text',
			selector: (row) => row.textEnabled,
			cell: (row) => (row.textEnabled ? <Check /> : '-'),
			sortable: true,
		},
		{
			name: 'Default',
			selector: (row) => row.isDefault,
			cell: (row) => (row.isDefault ? <Check /> : '-'),
			sortable: true,
		},
		{
			name: 'Created',
			selector: (row) => row.createdAt,
			cell: (row) => formatDate(row.createdAt, false),
			sortable: true,
			hide: theme.breakpoints.dataTable,
		},
		{
			name: 'Active / Deleted',
			selector: (row) => row.deletedAt,
			cell: (row) => (!row.deletedAt ? 'Active' : 'Deleted'),
			sortable: true,
			sortFunction: (rowA, rowB, direction) => {
				const a = rowA.deletedAt
					? new Date(rowA.deletedAt).getTime()
					: null
				const b = rowB.deletedAt
					? new Date(rowB.deletedAt).getTime()
					: null
				if (a === null && b === null) return 0

				if (direction === 'asc') {
					if (a === null) return 1 // nulls go last
					if (b === null) return -1
					return a - b
				}
				if (a === null) return -1 // nulls go first
				if (b === null) return 1
				return b - a
			},
			hide: theme.breakpoints.dataTable,
		},
	]

	const actionItems = [
		{
			name: 'Edit',
			onClick: (e, row) => onRowClicked(row),
		},
		{
			name: 'Delete',
			onClick: (e, row) => showRemoveModal(row),
			hideIf: (row) => row.deletedAt !== null || row.isDefault,
		},
		{
			name: 'Restore',
			onClick: (e, row) => showRestoreModal(row),
			hideIf: (row) => row.deletedAt === null,
		},
	]

	const {
		isFetching,
		isRefetching,
		error: errorFetchingNotificationTemplates,
		data: notificationTemplatesResult,
		refetch,
	} = useQuery({
		queryKey: [queryKeys.notificationTemplates],
		queryFn: () =>
			TheBigPOSApi.getNotificationTemplates({ showAll: true }),
		retry: 2,
	})

	const populatedTableData = useMemo(() => {
		return filteredData?.map((row) => {
			const templateType = notificationTemplateTypes.find(
				(t) => t.value === row.type
			)
			return { ...row, type: templateType ? templateType.label : '' }
		})
	}, [filteredData])

	useEffect(() => {
		if (errorFetchingNotificationTemplates) {
			alert(
				'There was a problem loading the notification templates',
				{
					severity: 'error',
				}
			)
		}
	}, [errorFetchingNotificationTemplates])

	useEffect(() => {
		if (
			notificationTemplatesResult?.data &&
			(searchText !== '' ||
				filters.active !== 'all' ||
				filters.hideDefaults)
		) {
			const lowercasedSearchText = searchText.toLowerCase()
			let newFilteredData = !searchText
				? notificationTemplatesResult.data
				: notificationTemplatesResult.data.filter(
						(d) =>
							d.name?.toLowerCase().includes(lowercasedSearchText) ||
							d.description
								?.toLowerCase()
								.includes(lowercasedSearchText) ||
							d.subject?.toLowerCase().includes(lowercasedSearchText)
					)
			newFilteredData = newFilteredData.filter((d) => {
				if (filters.active !== 'all') {
					if (filters.active === 'active' && d.deletedAt) return false
					else if (filters.active === 'deleted' && !d.deletedAt)
						return false
				}
				return !(filters.hideDefaults && d.isDefault)
			})
			setFilteredData(newFilteredData)
		} else {
			setFilteredData(notificationTemplatesResult?.data)
		}
	}, [notificationTemplatesResult, searchText, filters])

	const invalidateQueries = async () => {
		await queryClient.invalidateQueries({
			queryKey: [
				queryKeys.notificationTemplates,
				queryKeys.notificationTemplate,
			],
		})
		await refetch()
	}

	const handleMutationSuccess = (
		eventType,
		modalSetter,
		successMessage
	) => {
		const { id, name } = activeRow
		mixpanel.trackEvent(eventType, {
			id,
			name,
		})
		modalSetter(false)
		alert(
			`Notification Template "${name}" successfully ${successMessage}`
		)
		setActiveRow(null)
	}

	const removeTemplateMutation = useMutation({
		mutationFn: (id) => TheBigPOSApi.deleteNotificationTemplate(id),
		onSuccess: () => {
			invalidateQueries()
			handleMutationSuccess(
				eventTypes.NOTIFICATION_TEMPLATE_DELETED,
				setRemoveModalVisible,
				'removed'
			)
		},
	})

	const restoreTemplateMutation = useMutation({
		mutationFn: (id) => TheBigPOSApi.restoreNotificationTemplate(id),
		onSuccess: () => {
			invalidateQueries()
			handleMutationSuccess(
				eventTypes.NOTIFICATION_TEMPLATE_RESTORED,
				setRestoreModalVisible,
				'restored'
			)
		},
	})

	const removeNotificationTemplate = async () => {
		try {
			if (activeRow?.id)
				await removeTemplateMutation.mutateAsync(activeRow?.id)
		} catch (e) {
			alert(getErrorMessage(e), { severity: 'error' })
		}
	}

	const restoreNotificationTemplate = async () => {
		try {
			if (activeRow?.id)
				await restoreTemplateMutation.mutateAsync(activeRow?.id)
		} catch (e) {
			alert(getErrorMessage(e), { severity: 'error' })
		}
	}

	const showRemoveModal = (row) => {
		setRemoveModalVisible(true)
		setActiveRow(row)
	}

	const showRestoreModal = (row) => {
		setRestoreModalVisible(true)
		setActiveRow(row)
	}

	const onAddClick = () => {
		navigate(
			generatePath(
				`/${navigationLinking.AdminNotificationTemplateEdit}`,
				{ id: 'new' }
			)
		)
	}

	const onRowClicked = (row) => {
		navigate(
			generatePath(
				`/${navigationLinking.AdminNotificationTemplateEdit}`,
				{ id: row.id }
			)
		)
	}

	const handleFilterChange = (e, field) => {
		setFilters((f) => ({ ...f, [field]: e.target.value }))
	}

	const handleCheckBoxChange = (field) => {
		setFilters((f) => ({ ...f, [field]: !f[field] }))
	}

	const onSearchChange = (e) => {
		setSearchText(e.target.value)
	}
	const onClearSearchClick = () => {
		setSearchText('')
	}

	return (
		<Page
			page="app-notification-templates"
			title="Notification Templates"
			isFullWidth
		>
			<ModalRemoveRestoreRecordConfirm
				removeModalVisible={removeModalVisible}
				setRemoveModalVisible={setRemoveModalVisible}
				remove={removeNotificationTemplate}
				row={activeRow}
				loading={removeTemplateMutation.isPending}
			/>
			<ModalRemoveRestoreRecordConfirm
				removeModalVisible={restoreModalVisible}
				setRemoveModalVisible={setRestoreModalVisible}
				restore={restoreNotificationTemplate}
				row={activeRow}
				loading={restoreTemplateMutation.isPending}
			/>
			<div className="pl-5 pr-5 pb-10 pt-5 h-full">
				<DataTable
					data={populatedTableData}
					columns={columns}
					defaultSortAsc={false}
					defaultSortFieldId="name"
					pagination={true}
					progressPending={isFetching || isRefetching}
					title="Notification Templates"
					onRefreshClick={refetch}
					keyField="id"
					onRowClicked={onRowClicked}
					onSearchChange={onSearchChange}
					searchText={searchText}
					onClearSearchClick={onClearSearchClick}
					searchFilters={
						<>
							<FormControl id="filterActive">
								<InputLabel>
									{formatMessage({ id: 'global.active_deleted' })}
								</InputLabel>
								<Select
									value={filters.active}
									onChange={(e) => handleFilterChange(e, 'active')}
									label={formatMessage({
										id: 'global.active_deleted',
									})}
								>
									<MenuItem value="active">Show Active Only</MenuItem>
									<MenuItem value="deleted">
										Show Deleted Only
									</MenuItem>
									<MenuItem value="all">Show All</MenuItem>
								</Select>
							</FormControl>
							<FormControlLabel
								label={
									<Typography className="dark:text-white">
										Hide Defaults
									</Typography>
								}
								className={`select-none`}
								control={
									<Checkbox
										id={`hideDefaults`}
										checked={filters.hideDefaults}
										onChange={(e) =>
											handleCheckBoxChange('hideDefaults')
										}
										inputProps={{ 'aria-label': 'controlled' }}
									/>
								}
							/>
						</>
					}
					exportEnabled={false}
					fixedHeader={true}
					expandableRowsComponent={NotificationExpandedRow}
					actionItems={actionItems}
					addNewBtn={{
						text: 'Add Notification Template',
						onClick: onAddClick,
					}}
				/>
			</div>
		</Page>
	)
}
export default withAuth(AdminNotificationTemplates)
